// ─── Contexte IA partagé ────────────────────────────────────────────────────
// Utilisé par Dashboard, Analytics, Strategy pour enrichir les appels IA
// et fournir des conseils de secours quand les crédits sont épuisés.

export function buildAiContext(companyProfile = {}, strategyInstances = [], contacts = []) {
  const cp = companyProfile;
  const lines = [];

  if (cp.name)                  lines.push(`Entreprise : ${cp.name}`);
  if (cp.sector)                lines.push(`Secteur : ${cp.sector}`);
  if (cp.description)           lines.push(`Description : ${cp.description}`);
  if (cp.services)              lines.push(`Services/produits : ${cp.services}`);
  if (cp.unique_selling_point)  lines.push(`Argument unique : ${cp.unique_selling_point}`);
  if (cp.target_audience)       lines.push(`Clientèle cible : ${cp.target_audience}`);
  if (cp.communication_tone)    lines.push(`Ton : ${cp.communication_tone}`);
  if (cp.city)                  lines.push(`Ville : ${cp.city}`);

  const nets = ['facebook','instagram','linkedin','tiktok','google_mybusiness']
    .filter(n => cp[`${n}_url`] || (n === 'google_mybusiness' && cp.gmb_has_page));
  if (nets.length > 0) lines.push(`Réseaux actifs : ${nets.join(', ')}`);

  if (cp.marketing_audit?.overall_score != null) {
    lines.push(`Audit marketing : ${cp.marketing_audit.overall_score}/100`);
    if (cp.marketing_audit.top_priority) lines.push(`Priorité audit : ${cp.marketing_audit.top_priority}`);
  }

  const active = (strategyInstances || []).find(s => !s.completed);
  if (active) {
    lines.push(`Stratégie active : ${active.title}`);
    if (active.briefing?.objective) lines.push(`Objectif : ${active.briefing.objective}`);
    if (active.briefing?.context)   lines.push(`Contexte stratégie : ${active.briefing.context?.slice(0, 200)}`);
    const total = (active.phases || []).flatMap(p => p.actions || []).length;
    const done  = (active.phases || []).flatMap(p => p.actions || []).filter(a => a.done).length;
    if (total > 0) lines.push(`Progression : ${Math.round(done/total*100)}% (${done}/${total} actions)`);
  }

  if ((contacts || []).length > 0) {
    lines.push(`Base contacts : ${contacts.length}`);
    const vips = contacts.filter(c => c.category === 'VIP').length;
    if (vips > 0) lines.push(`VIP : ${vips}`);
  }

  return lines.join('\n');
}

// ─── Conseils de secours par secteur (quand crédits IA épuisés) ─────────────
const FALLBACK_BY_SECTOR = {
  'coiffure': [
    { icon: '✂️', title: 'Relance les clients absents depuis 60 jours', body: 'Un SMS simple du type "Tu nous manques — profite de 10% sur ta prochaine visite" génère en moyenne 30% de rappels. Lance depuis Campagnes.', link: '/campaigns/new' },
    { icon: '🎂', title: 'Active les messages d\'anniversaire', body: 'Un message personnalisé le jour de l\'anniversaire avec une offre exclusive fidélise mieux que n\'importe quelle pub. Configure dans Anniversaires.', link: '/birthdays' },
    { icon: '📱', title: 'Publie une photo avant/après cette semaine', body: 'Les transformations coiffure génèrent 4x plus d\'engagement sur Instagram. Une photo par semaine suffit pour maintenir la visibilité.', link: '/strategy' },
  ],
  'esthétique': [
    { icon: '💆', title: 'Propose un forfait soin + offre fidélité', body: 'Les clientes qui achètent un forfait reviennent 3x plus souvent. Crée une campagne email ciblée sur tes clientes VIP.', link: '/campaigns/new' },
    { icon: '🎂', title: 'Anniversaire avec cadeau surprise', body: 'Offrir un soin gratuit ou une réduction le mois de l\'anniversaire crée un lien émotionnel fort. Configure les messages auto.', link: '/birthdays' },
    { icon: '⭐', title: 'Demande des avis Google à tes meilleures clientes', body: '87% des consommateurs lisent les avis avant de choisir. Identifie tes VIPs et envoie un SMS de remerciement avec le lien direct.', link: '/contacts' },
  ],
  'restaurant': [
    { icon: '🍽️', title: 'Réactive les habitués absents depuis 45 jours', body: 'La fréquence de visite est la clé. Un SMS "On ne vous a pas vu depuis un moment — table pour deux ce weekend ?" fonctionne très bien.', link: '/campaigns/new' },
    { icon: '🎂', title: 'Offre spéciale le jour d\'anniversaire', body: 'Un dessert offert ou un verre de champagne le jour J transforme un client en ambassadeur. Active les messages auto.', link: '/birthdays' },
    { icon: '📅', title: 'Communique sur tes événements à venir', body: 'Soirées thématiques, menus spéciaux, événements privés — informer ta base 2 semaines à l\'avance double les réservations.', link: '/campaigns/new' },
  ],
  'retail': [
    { icon: '🛍️', title: 'Segment tes clients par fréquence d\'achat', body: 'Les clients qui achètent 3x+ méritent un traitement VIP. Classe-les et crée une offre exclusive pour ce groupe.', link: '/contacts' },
    { icon: '🔔', title: 'Relance avant les fêtes commerciales', body: 'Saint-Valentin, fête des Mères, Noël — prépare tes campagnes 3 semaines à l\'avance. Utilise les templates existants.', link: '/campaigns/new' },
    { icon: '🎂', title: 'Bon d\'achat anniversaire', body: 'Un bon d\'achat de 10-15% envoyé 1 semaine avant l\'anniversaire génère un taux de conversion de 40%. Active l\'automatisation.', link: '/birthdays' },
  ],
  'default': [
    { icon: '📧', title: 'Envoie une campagne à tes clients VIP cette semaine', body: 'Tes clients VIP génèrent en moyenne 3x plus de revenus que les autres. Une communication personnalisée renforce leur fidélité.', link: '/campaigns/new' },
    { icon: '🎂', title: 'Active les messages d\'anniversaire automatiques', body: 'Les messages d\'anniversaire ont un taux d\'ouverture de 80%+. Une fois configuré, Appee s\'en occupe entièrement.', link: '/birthdays' },
    { icon: '💤', title: 'Réactive tes contacts dormants', body: 'Les clients qui n\'ont pas eu de nouvelles depuis 6 mois sont à risque de partir. Une campagne de réactivation simple peut en récupérer 20-30%.', link: '/campaigns/new' },
    { icon: '📊', title: 'Lance un audit marketing', body: 'L\'audit analyse ton positionnement, tes réseaux et tes opportunités. Il guide Appee pour personnaliser tous ses conseils.', link: '/analytics' },
  ],
};

export function getFallbackAdvice(companyProfile = {}) {
  const sector = (companyProfile.sector || '').toLowerCase();
  const matched = Object.keys(FALLBACK_BY_SECTOR).find(k => k !== 'default' && sector.includes(k));
  return FALLBACK_BY_SECTOR[matched] || FALLBACK_BY_SECTOR['default'];
}

export function getFallbackAnalyticsInsights(companyProfile = {}, contacts = []) {
  const sector    = companyProfile.sector || '';
  const total     = contacts.length;
  const vipCount  = contacts.filter(c => c.category === 'VIP').length;
  const withEmail = contacts.filter(c => c.email).length;
  const withBday  = contacts.filter(c => c.birth_date).length;
  const emailPct  = total > 0 ? Math.round(withEmail / total * 100) : 0;
  const bdayPct   = total > 0 ? Math.round(withBday  / total * 100) : 0;

  return {
    sanite: {
      analyse:    `${emailPct}% de tes contacts ont un e-mail — ${bdayPct}% ont une date d'anniversaire.`,
      conseil:    emailPct < 80
        ? 'Complète les e-mails manquants pour activer tous les envois automatiques.'
        : 'Ta base est bien renseignée — concentre-toi sur la qualité de tes messages.',
      link:       emailPct < 80 ? '/contacts?noEmail=1' : '/campaigns/new',
      link_label: emailPct < 80 ? 'Contacts sans e-mail' : 'Créer une campagne',
    },
    portrait: {
      analyse:    `${vipCount} client${vipCount !== 1 ? 's' : ''} VIP identifié${vipCount !== 1 ? 's' : ''} sur ${total} contacts.`,
      conseil:    vipCount === 0
        ? 'Classe tes meilleurs clients en VIP — ils génèrent en moyenne 3x plus de valeur.'
        : `Tes ${vipCount} VIP sont ton actif le plus précieux. Envoie-leur une communication exclusive.`,
      link:       '/contacts',
      link_label: 'Gérer les contacts',
    },
    relation: {
      analyse:    'Les messages personnalisés génèrent 6x plus d\'engagement que les messages génériques.',
      conseil:    'Configure les envois anniversaire automatiques — Appee les gère sans intervention de ta part.',
      link:       '/birthdays',
      link_label: 'Voir les anniversaires',
    },
    geo: {
      analyse:    total > 0
        ? `${total} contact${total !== 1 ? 's' : ''} dans ta base de données.`
        : 'Aucun contact dans la base.',
      conseil:    total < 50
        ? 'Importe plus de clients pour enrichir ton analyse géographique.'
        : 'Ta base est prête pour des campagnes ciblées par ville.',
      link:       total < 50 ? '/imports' : null,
      link_label: total < 50 ? 'Importer des contacts' : null,
    },
    qualification: {
      analyse:    `${bdayPct}% de tes contacts ont une date d'anniversaire — c'est la donnée clé pour Appee.`,
      conseil:    bdayPct < 70
        ? 'Enrichis les fiches contacts avec les dates d\'anniversaire pour activer les automatisations.'
        : 'Excellente base — lance une stratégie pour maximiser ton taux d\'ouverture.',
      link:       bdayPct < 70 ? '/imports' : '/strategy',
      link_label: bdayPct < 70 ? 'Importer avec anniversaires' : 'Voir les stratégies',
    },
    actions: {
      analyse:    sector
        ? `Dans le secteur ${sector}, la régularité de communication est la clé de la fidélisation.`
        : 'La régularité est la clé — un contact bien suivi revient 3x plus souvent.',
      conseil:    'Lance une campagne de réactivation pour les clients sans interaction depuis 3 mois.',
      link:       '/campaigns/new',
      link_label: 'Créer une campagne',
    },
    _isFallback: true,
  };
}

export function getFallbackDashboardInsight(companyProfile = {}, contacts = []) {
  const name = companyProfile.name || 'ton entreprise';
  const sector = companyProfile.sector ? ` dans le secteur ${companyProfile.sector}` : '';
  const vips = contacts.filter(c => c.category === 'VIP').length;
  const withBday = contacts.filter(c => c.birth_date).length;
  const pct = contacts.length > 0 ? Math.round(withBday / contacts.length * 100) : 0;

  return {
    greeting: `Voici un aperçu de ${name}${sector}. ${vips > 0 ? `Tu as ${vips} contact${vips > 1 ? 's' : ''} VIP à chouchouter. ` : ''}${pct < 50 ? `Seulement ${pct}% de tes contacts ont une date d'anniversaire — en en ajoutant plus, Appee peut faire bien plus pour toi.` : `${pct}% de tes contacts ont un anniversaire — Appee peut leur envoyer des messages automatiquement.`}`,
    mission: {
      icon: '💡',
      title: 'Conseils IA désactivés — crédits épuisés',
      body: 'Les recommandations ci-dessous sont des conseils généraux. Pour des conseils personnalisés basés sur ton profil, tes données et ton audit, rachète des crédits IA.',
      cta: 'Racheter des crédits',
      link: '/settings?tab=plan',
    },
    _isFallback: true,
  };
}
